import { useState, useEffect, useCallback, useRef } from 'react';
import { CreditCard, AlertCircle, Loader2 } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import { formatSignedUsd } from '@/lib/money-format';
import { safeJsonParse } from './safe-json';
import { formatLedgerDate } from './ledger-labels';
import { usedMicroUsd, hasRemainders, lotStatusKey, type WalletLotsResponse } from './credit-lots';

/**
 * A user's credits, one row per lot, with what is left and when it expires.
 *
 * Fetches its own data rather than taking it as a prop, matching PricingCard
 * and LedgerCard: a failure here must not take the balance panel down with it.
 */
export function CreditLotsCard({ bare = false }: {
  /** Body only, for the billing page's records/credits tabs, whose header
   *  replaces this one (the balance above already states what remains). */
  bare?: boolean;
} = {}) {
  const { t, locale } = useI18n();
  const [data, setData] = useState<WalletLotsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const started = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const response = await fetch('/api/wallet/lots', { credentials: 'include' });
      if (!response.ok) throw new Error(`lots ${response.status}`);
      const parsed = await safeJsonParse<WalletLotsResponse>(response);
      if (!parsed) throw new Error('lots payload was not JSON');
      setData(parsed);
    } catch (err) {
      console.error('Error fetching wallet lots:', err);
      // Never fall through to the empty state: telling someone who has credits
      // that they have none is worse than telling them the page failed.
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    load();
  }, [load]);

  const lots = data?.lots ?? [];
  const showRemainders = hasRemainders(lots);

  const body = (
      <div className="wallet-card__content">
        {failed ? (
          <div className="wallet-card__unavailable">
            <AlertCircle size={24} />
            <p>{t('dashboard.wallet.lots.error')}</p>
            <button type="button" className="wallet-card__submit-btn" disabled={loading} onClick={load}>
              {t('dashboard.wallet.lots.retry')}
            </button>
          </div>
        ) : loading ? (
          <div className="wallet-card__loading"><Loader2 className="animate-spin" size={20} /></div>
        ) : lots.length === 0 ? (
          <div className="wallet-card__empty"><p>{t('dashboard.wallet.lots.empty')}</p></div>
        ) : (
          <div className="wallet-card__table-scroll">
            <table className="wallet-lots">
              <thead>
                <tr>
                  <th>{t('dashboard.wallet.lots.colCreated')}</th>
                  <th>{t('dashboard.wallet.lots.colAmount')}</th>
                  {showRemainders && <th>{t('dashboard.wallet.lots.colUsed')}</th>}
                  {showRemainders && <th>{t('dashboard.wallet.lots.colRemaining')}</th>}
                  <th>{t('dashboard.wallet.lots.colExpires')}</th>
                  <th>{t('dashboard.wallet.lots.colStatus')}</th>
                </tr>
              </thead>
              <tbody>
                {lots.map((lot) => {
                  const used = usedMicroUsd(lot);
                  return (
                    <tr key={lot.id}>
                      <td>{formatLedgerDate(lot.createdAt, locale)}</td>
                      <td>{formatSignedUsd(lot.amountMicroUsd)}</td>
                      {showRemainders && <td>{used === null ? '' : formatSignedUsd(used)}</td>}
                      {showRemainders && (
                        <td>{lot.remainingMicroUsd === undefined ? '' : formatSignedUsd(lot.remainingMicroUsd)}</td>
                      )}
                      <td>{formatLedgerDate(lot.expiresAt, locale)}</td>
                      <td>
                        <span className={`wallet-lots__status wallet-lots__status--${lot.status}`}>
                          {t(lotStatusKey(lot.status))}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
  );

  if (bare) return body;

  return (
    <div className="wallet-card wallet-card--full">
      <div className="wallet-card__header">
        <CreditCard size={20} />
        <h2>{t('dashboard.wallet.lots.title')}</h2>
        {data?.totalRemainingMicroUsd !== undefined && (
          <span className="wallet-card__header-stat">
            {t('dashboard.wallet.lots.totalRemaining')} {formatSignedUsd(data.totalRemainingMicroUsd)}
          </span>
        )}
      </div>
      {body}
    </div>
  );
}
